import { useEffect, useLayoutEffect, useRef } from 'react';

// This beautiful hook will execute a callback at a given time, unless the tab is not visible
// When the tab becomes visible again, the callback will be executed immediately (or at it's original schedule, if it is in the future)
// When executeAt is changed, the old schedule is replaced with the new one (or just canceled if executeAt is undefined)
//
// A given executeAt fires at most once. Without that guard, an executeAt in the past made the
// visibilitychange handler below recompute a negative delay and re-fire on every single tab focus,
// for the life of the tab — which is how a callback that fails to reschedule itself turns into a
// flood. See useConfigureAccessToken.
export function useDelayedExecution(callback: () => void, executeAt: Date | undefined) {
  const savedCallback = useRef(callback);

  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the timeout.
  useEffect(() => {
    if (!executeAt) {
      return;
    }

    let hasExecuted = false;
    let timeoutId: NodeJS.Timeout;

    const execute = () => {
      if (hasExecuted) return;
      hasExecuted = true;
      savedCallback.current();
    };

    const delayUntilExecuteAt = () => Math.max(0, executeAt.getTime() - Date.now());

    // Stop the scheduled execution when the tab is not visible and resume when it becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (hasExecuted) return;
        timeoutId = setTimeout(execute, delayUntilExecuteAt());
      } else {
        clearTimeout(timeoutId);
      }
    };

    if (document.visibilityState === 'visible') {
      timeoutId = setTimeout(execute, delayUntilExecuteAt());
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearTimeout(timeoutId);
    };
  }, [executeAt]);
}
